import { LOGOUT } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import type { CasinoSharedAction, CasinoSharedThunkAction } from '~/components/casino-shared/ducks/types';
import {
	assertIsTournamentWinType,
	Campaign,
	CampaignOld,
	EnhancedCampaign,
	EnhancedCampaignImage,
	EnhancedCampaignOld,
	EnhancedCategory,
	EnhancedGame
} from '~/components/casino-shared/types';
import enhanceImageView from '~/components/casino-shared/utilities/enhanceImage';
import getGameUrls from '~/components/casino-shared/utilities/getGameUrls';
import { showNotification } from '~/components/notifications/ducks/notifications';
import { SET_GRAPHYTE_CASINO_EXPLORE_RECOMMENDATIONS } from '~/components/search/ducks/action-names';
import { createImageViewFactory, generateCampaignImageSrcSetFactory } from '~/components/utilities/image-format';
import { BaseResponse, requestApi } from '~/services/api-new';
import { ExploreResponse } from '../types';

import { EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS } from '~/components/casino-shared/exclusive-game-widget/notification/action-names';
import {
	EXPLORE_CASINO_PROCESS,
	FETCH_CASINO_GAMES_START,
	HYDRATE_CASINO_GAMES,
	INVALIDATE_CASINO_DATA,
	PREVIOUS_LOCATION,
	SET_QUICK_FILTERS_POSITION,
	RESET_CASINO_EXPLORE_STATUS
} from './action-names';
import { EXPLORE_CASINO_ERROR, EXPLORE_CASINO_LOADING, EXPLORE_CASINO_SUCCESS, State } from './types';

const REDUCER_NAME = 'casino';
const host = '/';

const setPreviousLocation = (location?: { pathname: string; search: string }) => {
	return {
		type: PREVIOUS_LOCATION,
		payload: { previousLocation: location }
	};
};

const invalidateCasinoData = () => ({ type: INVALIDATE_CASINO_DATA });

const resetCasinoExploreStatus = () => ({ type: RESET_CASINO_EXPLORE_STATUS });

const fetchCasinoGames =
	(): CasinoSharedThunkAction =>
	async (
		dispatch,
		getState,
		{
			imageDescriptors,
			imageFormats,
			campaignBackgroundImageDescriptors,
			campaignForegroundImageDescriptors,
			license
		}
	) => {
		const state = getState();
		const {
			authentication: { authenticated },
			casino: { exploreCasinoStatus, wasAuthenticated },
			brand: { enableBannerService }
		} = state;

		if (
			exploreCasinoStatus === EXPLORE_CASINO_LOADING ||
			(exploreCasinoStatus === EXPLORE_CASINO_SUCCESS && authenticated === wasAuthenticated)
		) {
			return;
		}

		dispatch({
			type: FETCH_CASINO_GAMES_START
		});

		let counter = 0;

		const tryRetrieveData = async () => {
			dispatch({
				type: EXPLORE_CASINO_PROCESS,
				payload: EXPLORE_CASINO_LOADING
			});
			try {
				// TODO - remove later, need till not on stage
				const additionalHeaders = {
					'x-comeon-explore-version': 'new'
				};
				const response = await requestApi<BaseResponse<ExploreResponse>>({
					endpoint: `${host}rest/state/casino/explore`,
					additionalHeaders
				});

				if (response.status === 'FAILURE') {
					dispatch({
						type: EXPLORE_CASINO_PROCESS,
						payload: EXPLORE_CASINO_ERROR
					});

					if (response?.errorDescription) {
						dispatch(
							showNotification({
								message: response?.errorDescription,
								color: 'red',
								autoHide: true
							})
						);
					} else {
						dispatch(
							showNotification({
								messageKey: 'CASINO_EXPLORE_NO_DATA_RETURNED_FROM_BACKEND',
								color: 'red',
								autoHide: true
							})
						);
					}

					const startTimeOut = setTimeout(() => {
						counter += 1;
						if (counter > 4) {
							clearTimeout(startTimeOut);
							return;
						}
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						tryRetrieveData();
					}, 100 * counter);
					return;
				}

				dispatch({
					type: SET_QUICK_FILTERS_POSITION,
					payload: Number(response.result.quickFiltersPosition)
				});

				dispatch({
					type: HYDRATE_CASINO_GAMES,
					payload: {
						hydrated: true,
						wasAuthenticated: authenticated,
						response: response.result,
						imageDescriptors,
						imageFormats,
						campaignBackgroundImageDescriptors,
						campaignForegroundImageDescriptors,
						enableBannerService,
						license
					}
				});

				const { casino } = getState();
				const { exploreCategories, games } = casino;
				const firstRecommendedCategory = exploreCategories.find(category => category.type === 'recommended');
				const recommendedGames = firstRecommendedCategory?.gameCodes.map(gameCode => games[gameCode]);

				if (recommendedGames) {
					// fills the smart search results with an initial set of recommended games
					dispatch({
						type: SET_GRAPHYTE_CASINO_EXPLORE_RECOMMENDATIONS,
						payload: recommendedGames
					});
				}
			} catch (e) {
				dispatch({
					type: EXPLORE_CASINO_PROCESS,
					payload: EXPLORE_CASINO_ERROR
				});

				const startTimeOut = setTimeout(() => {
					counter += 1;
					if (counter > 4) {
						clearTimeout(startTimeOut);
						return;
					}
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					tryRetrieveData();
				}, 100 * counter);
			}
		};
		await tryRetrieveData();
	};

// Type assertion based on enableBannerService flag
function isCampaignArray(
	enableBannerService: boolean,
	_campaigns: Campaign[] | CampaignOld[]
): _campaigns is Campaign[] {
	return enableBannerService;
}

const initialState: State = {
	hydrated: false,
	wasAuthenticated: false,
	games: {},
	gameNamesByIdx: [],
	gameCodesByIdx: [],
	gameCodesByProviderCode: {},
	gameCodesBySubProviderCode: {},
	loading: false,
	previousLocation: {
		pathname: '',
		search: ''
	},
	campaigns: [],
	exploreCategories: [],
	exploreCasinoStatus: null,
	providers: {},
	subProviders: {},
	quickFiltersPosition: null
};

const reducer = (stateTemp = initialState, action: CasinoSharedAction) => {
	const state = stateTemp.initialized ? stateTemp : { ...initialState, ...stateTemp, initialized: true };
	switch (action.type) {
		case INVALIDATE_CASINO_DATA:
			return {
				...state,
				hydrated: false
			};
		case RESET_CASINO_EXPLORE_STATUS:
			return {
				...state,
				exploreCasinoStatus: initialState.exploreCasinoStatus
			};
		case LOGOUT:
			return {
				...state,
				exploreCasinoStatus: ''
			};
		case FETCH_CASINO_GAMES_START:
			return {
				...state,
				loading: true,
				hydrated: false,
				gameNamesByIdx: [],
				gameCodesByIdx: [],
				gameCodesByProviderCode: {},
				gameCodesBySubProviderCode: {}
			};
		case HYDRATE_CASINO_GAMES: {
			const gameNamesByIdx = state.gameNamesByIdx.slice();
			const gameCodesByIdx = state.gameCodesByIdx.slice();
			const gameCodesByProviderCode = { ...state.gameCodesByProviderCode };
			const gameCodesBySubProviderCode = { ...state.gameCodesBySubProviderCode };

			const providers = { ...state.providers };
			const subProviders = { ...state.subProviders };

			const {
				imageDescriptors,
				imageFormats,
				response,
				campaignBackgroundImageDescriptors,
				campaignForegroundImageDescriptors,
				enableBannerService,
				license
			} = action.payload;

			const formatEnhancer = createImageViewFactory(imageFormats, imageDescriptors);

			const { campaigns, categories, games } = response;

			const fetchedGames = games.reduce<Record<string, Partial<EnhancedGame>>>((acc, currGame, gameIndex) => {
				const game = { ...currGame, ...getGameUrls(currGame, license) };

				gameNamesByIdx[gameIndex] = game.name;
				gameCodesByIdx[gameIndex] = game.code;

				if (gameCodesByProviderCode[game.provider.code]) {
					gameCodesByProviderCode[game.provider.code].push(game.code);
				} else {
					gameCodesByProviderCode[game.provider.code] = [game.code];
				}
				providers[game.provider.code] = game.provider;

				if (game.casinoSubProviderVO) {
					if (gameCodesBySubProviderCode[game.casinoSubProviderVO.code]) {
						gameCodesBySubProviderCode[game.casinoSubProviderVO.code].push(game.code);
					} else {
						gameCodesBySubProviderCode[game.casinoSubProviderVO.code] = [game.code];
					}
					subProviders[game.casinoSubProviderVO.code] = game.casinoSubProviderVO;
				}

				providers[game.provider.code] = { ...game.provider };

				const enhancedGame: Partial<EnhancedGame> = {
					...game,
					imageView: enhanceImageView(game.imageView, imageDescriptors, formatEnhancer, false)
				};

				acc[game.code] = enhancedGame;

				return acc;
			}, {});

			const enhanceCampaignImage = generateCampaignImageSrcSetFactory(
				campaignBackgroundImageDescriptors || [],
				campaignForegroundImageDescriptors || []
			);

			let enhancedCampaigns: EnhancedCampaign[] | EnhancedCampaignOld[] = [];

			if (isCampaignArray(enableBannerService, campaigns)) {
				enhancedCampaigns = campaigns.map(banner => ({
					...banner,
					backgroundImage: enhanceCampaignImage(
						banner.backgroundImage,
						'background'
					) as EnhancedCampaignImage,
					foregroundImage1: banner.foregroundImage1
						? enhanceCampaignImage(banner.foregroundImage1, 'foreground')
						: undefined,
					foregroundImage2: banner.foregroundImage2
						? enhanceCampaignImage(banner.foregroundImage2, 'foreground')
						: undefined,
					tcImage: banner.tcImage ? enhanceCampaignImage(banner.tcImage, 'foreground') : undefined
				}));
			} else {
				enhancedCampaigns = campaigns.map(campaign => ({
					...campaign,
					backgroundImage: enhanceCampaignImage(
						campaign.backgroundImage,
						'background'
					) as EnhancedCampaignImage,
					foregroundImage: enhanceCampaignImage(
						campaign.foregroundImage,
						'foreground'
					) as EnhancedCampaignImage
				}));
			}

			const enhancedCategories: EnhancedCategory[] = categories.map(category => {
				if (category.type === 'winner_feed') {
					return {
						...category,
						winners: category.winners
							.filter(winner => (assertIsTournamentWinType(winner) ? true : winner.gameIdx !== undefined))
							.map(winner => ({
								...winner,
								...(!assertIsTournamentWinType(winner) ? { gameCode: games[winner.gameIdx].code } : {})
							}))
					};
				}

				return {
					...category,
					...(category.gamesByIdx
						? {
								gameCodes: category.gamesByIdx.map(index => games[index].code)
							}
						: null)
				};
			});

			return {
				...state,
				loading: false,
				hydrated: action.payload.hydrated,
				wasAuthenticated: action.payload.wasAuthenticated,
				games: {
					...state.games,
					...fetchedGames
				},
				gameNamesByIdx,
				gameCodesByIdx,
				gameCodesByProviderCode,
				gameCodesBySubProviderCode,
				providers,
				subProviders,
				campaigns: enhancedCampaigns,
				exploreCategories: enhancedCategories,
				exploreCasinoStatus: EXPLORE_CASINO_SUCCESS
			};
		}
		case PREVIOUS_LOCATION: {
			return {
				...state,
				...action.payload
			};
		}
		case EXPLORE_CASINO_PROCESS: {
			return {
				...state,
				exploreCasinoStatus: action.payload
			};
		}
		case EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS: {
			return {
				...state,
				exploreCategories: state.exploreCategories.map(category => {
					if (category.type !== 'exclusive_game_widget' || !action.payload || action.payload.length === 0) {
						return category;
					}

					const updatedGameLaunch = action.payload.find(
						updatedWidget => updatedWidget.widgetId === category.widgetId
					)?.gameLaunch;

					if (updatedGameLaunch) {
						return {
							...category,
							gameLaunch: updatedGameLaunch
						};
					}
					return category;
				})
			};
		}
		case SET_QUICK_FILTERS_POSITION: {
			return {
				...state,
				quickFiltersPosition: action.payload
			};
		}
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export { fetchCasinoGames, invalidateCasinoData, resetCasinoExploreStatus, setPreviousLocation };

export default reducer;
